import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, hotMovies, latestMovies, type MovieItem } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState('latest');
  const [movies, setMovies] = useState<MovieItem[]>([]);

  const currentCategory = categories.find((c) => c.id === slug);
  const allMovies = [...hotMovies, ...latestMovies];

  useEffect(() => {
    let filtered: MovieItem[];
    if (slug === 'movie') {
      filtered = allMovies.filter(
        (m) => m.category === '悬疑' || m.category === '科幻' || m.category === '犯罪' || m.category === '战争' || m.category === '动作'
      );
    } else if (slug === 'tv') {
      filtered = allMovies.filter((m) => m.category === '电视剧');
    } else if (slug === 'variety') {
      filtered = allMovies.filter((m) => m.category === '综艺');
    } else if (slug === 'anime') {
      filtered = allMovies.filter((m) => m.category === '动画' || m.category === '动漫');
    } else if (slug === 'documentary') {
      filtered = allMovies.filter((m) => m.category === '纪录片');
    } else if (slug === 'kids') {
      filtered = allMovies.filter((m) => m.category === '少儿');
    } else {
      filtered = allMovies;
    }

    if (activeSort === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (activeSort === 'year') {
      filtered.sort((a, b) => b.year - a.year);
    }

    setMovies(filtered);
  }, [slug, activeSort, allMovies]);

  const sortOptions = [
    { value: 'latest', label: '最新' },
    { value: 'rating', label: '评分最高' },
    { value: 'year', label: '年份最新' },
  ];

  if (!currentCategory) {
    const notFoundSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': '页面未找到 - 四虎影院',
      'url': `https://realsmodel.cn/category/${slug}`,
      'inLanguage': 'zh-CN',
    };

    return (
      <>
        <SchemaOrg data={notFoundSchema} />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">分类不存在</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://realsmodel.cn/' },
      { '@type': 'ListItem', 'position': 2, 'name': currentCategory?.name || '', 'item': `https://realsmodel.cn/category/${slug}` },
    ],
  }), [currentCategory, slug]);

  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': `${currentCategory?.name || '分类'} - 四虎影院`,
    'description': `四虎影院${currentCategory?.name || '分类'}频道，共${currentCategory?.count.toLocaleString() || 0}部资源，海量高清${currentCategory?.name || ''}在线观看。`,
    'url': `https://realsmodel.cn/category/${slug}`,
    'inLanguage': 'zh-CN',
  }), [currentCategory, slug]);

  return (
    <>
      <SchemaOrg data={webPageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-950 pt-20 pb-8">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-200">{currentCategory.name}</span>
            </nav>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500">
                <i className={`${currentCategory.icon} text-xl text-white`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-50">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-400 mt-0.5">
                  共 {currentCategory.count.toLocaleString()} 部资源
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap ${
                    slug === cat.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-1 ml-auto bg-background-100 rounded-full p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setActiveSort(opt.value)}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSort === opt.value
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {movies.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-film-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的影视资源</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {movies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/article/${movie.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur-sm rounded text-xs text-yellow-400 font-semibold">
                      {movie.rating}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <div className="flex items-center justify-center gap-2 py-2 bg-primary-500/90 rounded-full text-white text-xs font-medium">
                        <i className="ri-play-circle-line"></i>
                        立即播放
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {movie.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-400">{movie.category}</span>
                    <span className="text-xs text-foreground-300">·</span>
                    <span className="text-xs text-foreground-400">{movie.updateInfo}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}